import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import {
  STORE_CATEGORY_PUBLIC_DESCRIPTION_MAX_LENGTH,
  useTranslation,
  validateStoreCategoryPublicDescription,
} from '@dukanti/shared';
import type {
  CategoryErrorKey,
  StoreCategoryPublicDescription,
  StoreCategoryWithMedia,
  TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { categoryFormSchema, type CategoryFormValues } from '../lib/category-form-schema';
import { CategoryMediaPicker } from './category-media-picker';

export interface CategoryFormSubmit {
  readonly name: string;
  readonly publicDescription: StoreCategoryPublicDescription | null;
  readonly image: File | null;
  readonly removeImage: boolean;
}

export interface CategoryFormDialogProps {
  readonly open: boolean;
  readonly category?: StoreCategoryWithMedia | null;
  readonly isSubmitting?: boolean;
  readonly errorKey?: CategoryErrorKey | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (values: CategoryFormSubmit) => Promise<void>;
}

export function CategoryFormDialog({
  open,
  category,
  isSubmitting = false,
  errorKey,
  onOpenChange,
  onSubmit,
}: CategoryFormDialogProps) {
  const { t, dir } = useTranslation();
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const isEdit = !!category;
  const emptyPublicDescription = useMemo<StoreCategoryPublicDescription>(
    () => ({ ar: '', he: '', en: '' }),
    [],
  );
  const defaultValues = useMemo<CategoryFormValues>(
    () => ({
      name: category?.name ?? '',
      image: null,
      publicDescription: category?.public_description ?? emptyPublicDescription,
    }),
    [category, emptyPublicDescription],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setRemoveExistingImage(false);
    }
  }, [defaultValues, open, reset]);

  const image = watch('image') ?? null;
  const existingUrl = removeExistingImage ? null : (category?.media?.url ?? null);

  const submit = handleSubmit(async (values) => {
    await onSubmit({
      name: values.name.trim(),
      publicDescription:
        validateStoreCategoryPublicDescription(values.publicDescription).value ?? null,
      image: values.image ?? null,
      removeImage: removeExistingImage,
    });
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent
        dir={dir}
        data-testid="category-form-dialog"
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>
            {t(isEdit ? 'admin.categories.form.title.edit' : 'admin.categories.form.title.create')}
          </DialogTitle>
          <DialogDescription>
            {t(
              isEdit
                ? 'admin.categories.form.description.edit'
                : 'admin.categories.form.description.create',
            )}
          </DialogDescription>
        </DialogHeader>

        <form noValidate onSubmit={submit} className="space-y-5">
          {errorKey ? (
            <div
              role="alert"
              className="border-destructive/25 bg-destructive/10 text-destructive flex items-start gap-2 rounded-md border px-4 py-3 text-start text-sm"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{t(errorKey as TranslationKey)}</span>
            </div>
          ) : null}

          <div>
            <Label htmlFor="category-name" className="mb-1.5 block text-start">
              {t('admin.categories.form.nameLabel')}
              <span className="text-terracotta ms-1" aria-hidden>
                *
              </span>
            </Label>
            <Input
              id="category-name"
              type="text"
              maxLength={80}
              autoComplete="off"
              placeholder={t('admin.categories.form.namePlaceholder')}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'category-name-error' : undefined}
              data-testid="category-name-input"
              {...register('name')}
            />
            {errors.name ? (
              <p
                id="category-name-error"
                role="alert"
                className="text-destructive mt-1 text-start text-xs"
              >
                {t(errors.name.message as TranslationKey)}
              </p>
            ) : null}
          </div>

          <CategoryMediaPicker
            file={image}
            existingUrl={existingUrl}
            errorKey={(errors.image?.message as TranslationKey | undefined) ?? null}
            disabled={isSubmitting}
            onFileChange={(file) =>
              setValue('image', file, { shouldDirty: true, shouldValidate: true })
            }
            onRemoveExisting={() => setRemoveExistingImage(true)}
          />

          <fieldset className="space-y-3">
            <legend className="text-foreground text-start text-sm font-semibold">
              {t('admin.categories.form.publicDescription.legend')}
            </legend>
            <div className="text-start">
              <p
                id="category-public-description-help"
                className="text-muted-foreground mt-1 text-sm leading-6"
              >
                {t('admin.categories.form.publicDescription.help')}
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {(
                [
                  ['ar', 'admin.categories.form.publicDescription.arLabel'],
                  ['he', 'admin.categories.form.publicDescription.heLabel'],
                  ['en', 'admin.categories.form.publicDescription.enLabel'],
                ] as const
              ).map(([locale, labelKey]) => {
                const fieldId = `category-public-description-${locale}`;
                const error = errors.publicDescription?.[locale];
                const errorId = error ? `${fieldId}-error` : undefined;
                return (
                  <div key={locale} className="min-w-0">
                    <Label htmlFor={fieldId} className="mb-1.5 block text-start">
                      {t(labelKey)}
                    </Label>
                    <Textarea
                      id={fieldId}
                      maxLength={STORE_CATEGORY_PUBLIC_DESCRIPTION_MAX_LENGTH}
                      rows={4}
                      placeholder={t('admin.categories.form.publicDescription.placeholder')}
                      aria-invalid={!!error}
                      aria-describedby={
                        errorId
                          ? `category-public-description-help ${errorId}`
                          : 'category-public-description-help'
                      }
                      data-testid={`category-public-description-${locale}`}
                      className="min-h-28 whitespace-normal break-words text-start"
                      {...register(`publicDescription.${locale}`)}
                    />
                    {error ? (
                      <p
                        id={errorId}
                        role="alert"
                        className="text-destructive mt-1 text-start text-xs"
                      >
                        {t(error.message as TranslationKey)}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </fieldset>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={isSubmitting}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting || undefined}>
              {isSubmitting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              <span>
                {t(
                  isEdit
                    ? 'admin.categories.form.submitEdit'
                    : 'admin.categories.form.submitCreate',
                )}
              </span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
